import { z } from "zod";
import { OpeningWindowSchema } from "@bytebite/schemas/hours";
import { RestaurantProfileSchema } from "@bytebite/schemas/profile";

/**
 * Public storefront restaurant payload.
 *
 * Derived solely from hostname-resolved tenant context. Does not include
 * internal status, domain ids, or timestamps.
 */
export const StorefrontRestaurantBrandingSchema = z.object({
  logoUrl: z.string().min(1).nullable(),
  primaryColor: z.string().min(1),
  secondaryColor: z.string().min(1),
});

export const StorefrontRestaurantHoursSchema = z.object({
  windows: z.array(OpeningWindowSchema),
});

export const StorefrontPresentationSchema = z.object({
  heroUrl: z.string().min(1).nullable(),
  heroAlt: z.string().min(1).nullable(),
  secondaryLogoUrl: z.string().min(1).nullable(),
  logoAlt: z.string().min(1).nullable(),
  seoTitle: z.string().min(1).nullable(),
  seoDescription: z.string().min(1).nullable(),
  socialImageUrl: z.string().min(1).nullable(),
  cuisines: z.array(z.string().min(1)),
});

export const StorefrontLegalSchema = z.object({
  operatorName: z.string().min(1),
  legalForm: z.string().min(1).nullable(),
  imprint: z.string().min(1),
  privacy: z.string().min(1),
  terms: z.string().min(1),
});

export const StorefrontRestaurantSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  slug: z.string().min(1),
  branding: StorefrontRestaurantBrandingSchema,
  hours: StorefrontRestaurantHoursSchema,
  deliveryAvailable: z.boolean(),
  ordersPaused: z.boolean(),
  profile: RestaurantProfileSchema.nullable(),
  presentation: StorefrontPresentationSchema.nullable(),
  legal: StorefrontLegalSchema.nullable(),
});

export type StorefrontRestaurant = z.infer<typeof StorefrontRestaurantSchema>;
export type StorefrontPresentation = z.infer<typeof StorefrontPresentationSchema>;
export type StorefrontLegal = z.infer<typeof StorefrontLegalSchema>;
export type StorefrontRestaurantBranding = z.infer<typeof StorefrontRestaurantBrandingSchema>;
export type StorefrontRestaurantHours = z.infer<typeof StorefrontRestaurantHoursSchema>;

/**
 * Stable public error envelope used by the API error handler.
 */
export const PublicApiErrorSchema = z.object({
  statusCode: z.number().int(),
  error: z.string(),
  message: z.string(),
});

export type PublicApiError = z.infer<typeof PublicApiErrorSchema>;
